/**
 * useDraft — autosave form state to localStorage so a crash, a refresh, or a
 * frozen tab mid-report never loses work.
 *
 * Usage:
 *   const draft = useDraft('incident:new', INITIAL_FORM);
 *   <textarea value={draft.value.description} onChange={(e) => draft.setValue({ ...draft.value, description: e.target.value })} />
 *   {draft.pendingDraft && <DraftRestorePrompt onRestore={draft.restoreDraft} onDiscard={draft.discardDraft} />}
 *   // on successful submit:
 *   draft.clearDraft();
 *
 * Semantics:
 *   - Storage key = `kennel:draft:<tenantId>:<key>` so drafts never leak
 *     across tenants on a shared device. tenantId comes from the staff auth
 *     context when available; otherwise a shared 'anon' bucket.
 *   - Writes are debounced (default 400ms) and flushed on unmount / key change.
 *   - Every storage access is wrapped in try/catch — private mode, quota,
 *     disabled storage and thumbnail sandboxes all degrade to "no autosave".
 *   - A value equal to `initial` (or empty) REMOVES the stored draft rather
 *     than persisting noise.
 *   - On mount (and whenever `key` changes) the hook does NOT silently apply a
 *     stored draft. It exposes it as `pendingDraft` so the UI can ask
 *     "Restore unsaved draft?" — restoring someone else's half-typed text
 *     without asking is worse than losing it.
 *   - Drafts older than 7 days are ignored and removed.
 */
import { useAuthContext } from '@/auth/useAuthContext';
import { useCallback, useEffect, useRef, useState } from 'react';

const PREFIX = 'kennel:draft';
const DEFAULT_DEBOUNCE_MS = 400;
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

interface StoredDraft<T> {
  v: T;
  savedAt: number;
}

function storage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

export function readDraft<T>(fullKey: string): T | null {
  try {
    const raw = storage()?.getItem(fullKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredDraft<T>;
    if (!parsed || typeof parsed !== 'object' || !('v' in parsed)) return null;
    if (typeof parsed.savedAt !== 'number' || Date.now() - parsed.savedAt > MAX_AGE_MS) {
      removeDraft(fullKey);
      return null;
    }
    return parsed.v;
  } catch {
    return null;
  }
}

export function writeDraft<T>(fullKey: string, v: T): void {
  try {
    const payload: StoredDraft<T> = { v, savedAt: Date.now() };
    storage()?.setItem(fullKey, JSON.stringify(payload));
  } catch {
    // quota / private mode / disabled storage — autosave silently degrades.
  }
}

export function removeDraft(fullKey: string): void {
  try {
    storage()?.removeItem(fullKey);
  } catch {
    // ignore
  }
}

export function draftStorageKey(tenantId: string, key: string): string {
  return `${PREFIX}:${tenantId}:${key}`;
}

function sameValue(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
}

function defaultIsEmpty(v: unknown): boolean {
  if (v === null || v === undefined) return true;
  if (typeof v === 'string') return v.trim().length === 0;
  if (Array.isArray(v)) return v.length === 0;
  return false;
}

/**
 * Resolve the active tenant id for key scoping. `useAuthContext` throws
 * outside an <AuthProvider> (customer portal, isolated component tests) —
 * fall back to a shared bucket rather than crashing the form.
 */
function useTenantId(): string {
  let tenantId = 'anon';
  try {
    const { state } = useAuthContext();
    tenantId = state.me?.activeAccount?.id ?? 'anon';
  } catch {
    // no provider in this tree
  }
  return tenantId;
}

export interface UseDraftOptions<T> {
  debounceMs?: number;
  /** Treat these values as "nothing to save" (stored draft removed). */
  isEmpty?: (v: T) => boolean;
}

export interface UseDraftResult<T> {
  value: T;
  setValue: (next: T | ((prev: T) => T)) => void;
  /** A stored draft that differs from `initial`, awaiting a restore/discard decision. */
  pendingDraft: T | null;
  restoreDraft: () => void;
  discardDraft: () => void;
  /** Call after a successful submit — removes the stored draft. */
  clearDraft: () => void;
  /** Fully-scoped storage key (null when `key` is null → autosave disabled). */
  storageKey: string | null;
}

export function useDraft<T>(
  key: string | null,
  initial: T,
  opts: UseDraftOptions<T> = {},
): UseDraftResult<T> {
  const tenantId = useTenantId();
  const storageKey = key ? draftStorageKey(tenantId, key) : null;
  const debounceMs = opts.debounceMs ?? DEFAULT_DEBOUNCE_MS;
  const isEmptyRef = useRef<(v: T) => boolean>(opts.isEmpty ?? defaultIsEmpty);
  isEmptyRef.current = opts.isEmpty ?? defaultIsEmpty;

  const [value, setValueState] = useState<T>(initial);
  const [pendingDraft, setPendingDraft] = useState<T | null>(null);
  const latestRef = useRef<T>(initial);
  const initialRef = useRef<T>(initial);
  const dirtyRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flush = useCallback((targetKey: string | null, v: T) => {
    if (!targetKey) return;
    if (isEmptyRef.current(v) || sameValue(v, initialRef.current)) removeDraft(targetKey);
    else writeDraft(targetKey, v);
  }, []);

  // Key change (incl. mount): reset to `initial`, cancel any pending write,
  // and surface a stored draft for the new key as `pendingDraft`.
  useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    dirtyRef.current = false;
    latestRef.current = initialRef.current;
    setValueState(initialRef.current);
    if (!storageKey) {
      setPendingDraft(null);
      return;
    }
    const stored = readDraft<T>(storageKey);
    if (stored !== null && !isEmptyRef.current(stored) && !sameValue(stored, initialRef.current)) {
      setPendingDraft(stored);
    } else {
      setPendingDraft(null);
    }
    return () => {
      // Flush an in-flight debounced write for the OLD key before switching.
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
        flush(storageKey, latestRef.current);
      }
    };
  }, [storageKey]);

  // Follow `initial` while the user hasn't edited (async-loaded values).
  // If the stored draft turns out to equal the loaded value, drop it.
  useEffect(() => {
    // Object initials are often re-created per render; only react to a
    // semantic change so an unstable-but-equal reference cannot loop.
    if (sameValue(initial, initialRef.current) && !Object.is(initial, initialRef.current)) {
      initialRef.current = initial;
      return;
    }
    initialRef.current = initial;
    if (!dirtyRef.current) {
      latestRef.current = initial;
      setValueState(initial);
    }
    setPendingDraft((p) => {
      if (p !== null && sameValue(p, initial)) {
        if (storageKey) removeDraft(storageKey);
        return null;
      }
      return p;
    });
  }, [initial]);

  const setValue = useCallback(
    (next: T | ((prev: T) => T)) => {
      const resolved =
        typeof next === 'function' ? (next as (prev: T) => T)(latestRef.current) : next;
      latestRef.current = resolved;
      dirtyRef.current = true;
      setValueState(resolved);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        flush(storageKey, latestRef.current);
      }, debounceMs);
    },
    [storageKey, debounceMs, flush],
  );

  const clearDraft = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    dirtyRef.current = false;
    if (storageKey) removeDraft(storageKey);
    setPendingDraft(null);
  }, [storageKey]);

  const discardDraft = useCallback(() => {
    if (storageKey) removeDraft(storageKey);
    setPendingDraft(null);
  }, [storageKey]);

  const restoreDraft = useCallback(() => {
    if (pendingDraft === null) return;
    latestRef.current = pendingDraft;
    dirtyRef.current = true;
    setValueState(pendingDraft);
    setPendingDraft(null);
  }, [pendingDraft]);

  return { value, setValue, pendingDraft, restoreDraft, discardDraft, clearDraft, storageKey };
}
